import { useEffect, useRef, useState } from "react";
import { useCopyToClipboard } from "react-use";
import Check from "@mui/icons-material/Check";
import ContentCopy from "@mui/icons-material/ContentCopy";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";

interface ShareLinkDialogProps {
  url: string | null;
  loading: boolean;
  error: boolean;
  open: boolean;
  onClose: () => void;
}

const ShareLinkDialog = ({ url, loading, error, open, onClose }: ShareLinkDialogProps) => {
  const [copied, setCopied] = useState(false);
  const [, copy] = useCopyToClipboard();
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
        copyTimeoutRef.current = null;
      }
    }
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, [open]);

  const handleCopy = () => {
    if (!url) {
      return;
    }
    copy(url);
    setCopied(true);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog fullWidth open={open} onClose={onClose}>
      <DialogTitle>Share link</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, fontSize: 14 }}>
        {loading && (
          <Typography variant="body2" color="text.secondary">
            Generating link…
          </Typography>
        )}
        {error && <Alert severity="error">Unable to generate share link.</Alert>}
        {url && (
          <>
            <TextField
              fullWidth
              size="small"
              value={url}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: "monospace", fontSize: 12 },
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleCopy} size="small" aria-label="Copy link">
                      {copied ? <Check fontSize="small" /> : <ContentCopy fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Expires in 1 hour.
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareLinkDialog;
